import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Download } from "lucide-react";
import { ELIA_CONCEPTS } from "@/components/brand/EliaLogos";
import { getSelectedElia, setSelectedElia } from "@/components/brand/EliaBrand";

/**
 * ELIA — Brand concepts preview.
 * Route: /brand/elia
 * Purpose: preview all 12 logo directions side-by-side, on light AND dark
 * backgrounds, at 3 sizes (favicon 16, avatar 40, hero 96). Select the default
 * concept used across the platform.
 */
export default function EliaBrandPreview() {
  const [selected, setSelected] = useState(getSelectedElia());
  const [bg, setBg] = useState("light");

  const toggle = (key) => {
    setSelectedElia(key);
    setSelected(key);
  };

  const bgClass = bg === "light" ? "bg-[#F5F0E6]" : bg === "dark" ? "bg-[#14213D]" : "bg-white";
  const foreground = bg === "dark" ? "#F5F0E6" : "#0B2545";
  const accent = "#B98A47";

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]">
      <header className="border-b border-[#E4E1D8] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xs font-mono uppercase tracking-[0.24em] text-[#5B5B62] hover:text-[#0B2545]">
            <ArrowLeft className="inline h-3.5 w-3.5 mr-1 -mt-0.5" strokeWidth={2} /> Accueil
          </Link>
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
            ALTIORA · Brand Preview · 12 directions
          </div>
          <div className="flex items-center gap-1 rounded-full bg-white border border-[#E4E1D8] p-1">
            {[["light", "Ivory"], ["white", "Blanc"], ["dark", "Navy"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setBg(k)}
                className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest transition-colors ${
                  bg === k ? "bg-[#0B2545] text-white" : "text-[#5B5B62]"
                }`}
              >{l}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14" data-testid="elia-brand-preview-page">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
            Décision stratégique · marque fondatrice
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            Choisir l&apos;identité <span className="text-[#0B2545]">ALTIORA</span> qui durera 20 ans.
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#5B5B62]">
            Douze directions créatives — wordmarks, symboles, monogrammes, combinaisons.
            Chaque concept fonctionne en favicon, sur carte de visite, dans une conférence,
            et sur des documents institutionnels. Sélectionnez celui qui vous accompagnera.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="elia-concepts-grid">
          {ELIA_CONCEPTS.map((c) => {
            const Comp = c.Component;
            const isSelected = selected === c.key;
            return (
              <button
                key={c.key}
                onClick={() => toggle(c.key)}
                data-testid={`elia-concept-${c.key}`}
                className={`text-left rounded-xl border bg-white p-5 transition-all shadow-sm hover:shadow-md ${
                  isSelected ? "border-[#0B2545] ring-2 ring-[#0B2545]/20" : "border-[#E4E1D8] hover:border-[#0B2545]/40"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                    #{String(c.id).padStart(2, "0")} · {c.family}
                  </span>
                  {isSelected && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#0B2545] text-white text-[9px] font-mono uppercase tracking-widest px-2 py-0.5">
                      <Check className="h-2.5 w-2.5" strokeWidth={2.5} /> Sélectionné
                    </span>
                  )}
                </div>

                {/* Preview large */}
                <div className={`${bgClass} rounded-lg p-8 flex items-center justify-center min-h-[130px] border border-[#E4E1D8]/60`}>
                  <Comp size={72} color={foreground} accent={accent} />
                </div>

                {/* Sizes row */}
                <div className={`${bgClass} mt-2 rounded-lg px-4 py-3 flex items-center justify-around gap-3 border border-[#E4E1D8]/60`}>
                  <div className="flex items-center gap-2">
                    <Comp size={16} color={foreground} accent={accent} />
                    <span className="text-[9px] font-mono text-[#8B8B8F]">16 px</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Comp size={28} color={foreground} accent={accent} />
                    <span className="text-[9px] font-mono text-[#8B8B8F]">28 px</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Comp size={40} color={foreground} accent={accent} />
                    <span className="text-[9px] font-mono text-[#8B8B8F]">40 px</span>
                  </div>
                </div>

                <div className="mt-4 font-display text-base text-[#1F2937] tracking-tight">{c.name}</div>
                <div className="mt-1 text-xs text-[#5B5B62] leading-relaxed">{c.intent}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-14 rounded-xl border border-[#E4E1D8] bg-white p-6" data-testid="elia-selection-panel">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
            Sélection actuelle
          </div>
          <div className="mt-2 font-display text-2xl text-[#1F2937] tracking-tight">
            {ELIA_CONCEPTS.find((c) => c.key === selected)?.name || "—"}
          </div>
          <p className="mt-2 text-sm text-[#5B5B62] leading-relaxed max-w-2xl">
            Ce concept est immédiatement utilisé dans la TopBar, la landing page, les emails et
            partout où ALTIORA est présente. Vous pouvez toujours revenir ici pour ajuster.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap text-xs font-mono uppercase tracking-widest">
            <span className="text-[#8B8B8F]">Clé stockée : </span>
            <code className="bg-[#F8F7F3] px-2 py-1 rounded text-[#0B2545]">{selected}</code>
          </div>
        </div>
      </main>
    </div>
  );
}
